// The client-portal sign-in policy. Lives in the gear on the Client
// portal row of /settings/integrations. Policy, not credentials: three
// switches, their provenance, and copy that says plainly what each door
// costs - the code door most of all.

import { useState } from "react";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import {
  usePortalPolicy,
  useSavePortalPolicy,
  useRemovePortalPolicy,
} from "../../hooks/usePortalPolicy";
import type { PortalOtpSecondFactor, PortalPolicyUpdate } from "../../api/integrations";

const TWO_STEP_HINTS: Record<PortalOtpSecondFactor, string> = {
  required:
    "A right password waits for a six-digit code mailed to the address on file. Stands down while the firm's outgoing mail is unproven, and never applies to the case ID door.",
  off: "A right password signs the client in.",
};

export function PortalSection() {
  // Mounted from the admin-only gear, but the flag stands on its own:
  // wherever this section lands, a non-admin never issues the GET.
  const user = useCurrentUser();
  const policyQ = usePortalPolicy(isAdminRole(user?.role));
  const save = useSavePortalPolicy();
  const remove = useRemovePortalPolicy();
  const [edits, setEdits] = useState<PortalPolicyUpdate>({});

  const status = policyQ.data;
  const twoStep = edits.otpSecondFactor ?? status?.otpSecondFactor ?? "off";
  const codeLogin = edits.otpLoginEnabled ?? status?.otpLoginEnabled ?? false;
  const caseIdDoor = edits.caseIdLoginEnabled ?? status?.caseIdLoginEnabled ?? true;

  // Only what differs from what is stored: the server audits every change
  // by value, and an idle Save would write noise into that trail.
  const update: PortalPolicyUpdate = {};
  if (status) {
    if (twoStep !== status.otpSecondFactor) update.otpSecondFactor = twoStep;
    if (codeLogin !== status.otpLoginEnabled) update.otpLoginEnabled = codeLogin;
    if (caseIdDoor !== status.caseIdLoginEnabled) update.caseIdLoginEnabled = caseIdDoor;
  }
  const dirty = Object.keys(update).length > 0;
  const busy = save.isPending || remove.isPending;

  function onSave() {
    save.mutate(update, { onSuccess: () => setEdits({}) });
  }

  function onRemove() {
    const ok = window.confirm(
      "Remove the portal sign-in settings? Every switch goes back to its default.",
    );
    if (!ok) return;
    remove.mutate(undefined, { onSuccess: () => setEdits({}) });
  }

  return (
    <section className="settings-section">
      <p className="settings-sub">How clients prove who they are at the portal door.</p>

      {policyQ.isLoading && (
        <SkeletonGroup>
          <Skeleton width={220} />
          <Skeleton width={180} />
        </SkeletonGroup>
      )}
      {policyQ.isError && (
        <p className="settings-error">Could not load the portal settings. Refresh to retry.</p>
      )}

      {status && (
        <>
          <div className="settings-field">
            <label className="settings-policy-select">
              <span className="settings-policy-title">Two-step sign-in</span>
              <select
                className="cc-field"
                aria-label="Two-step sign-in for clients"
                value={twoStep}
                disabled={busy}
                onChange={(e) =>
                  setEdits((prev) => ({
                    ...prev,
                    otpSecondFactor: e.target.value as PortalOtpSecondFactor,
                  }))
                }
              >
                <option value="off">Off</option>
                <option value="required">Required after the password</option>
              </select>
              <span className="settings-hint">{TWO_STEP_HINTS[twoStep]}</span>
            </label>

            <label className="settings-policy">
              <input
                type="checkbox"
                aria-label="Sign-in by emailed code"
                checked={codeLogin}
                disabled={busy}
                onChange={(e) =>
                  setEdits((prev) => ({ ...prev, otpLoginEnabled: e.target.checked }))
                }
              />
              <span className="settings-policy-switch" aria-hidden="true" />
              <span className="settings-policy-text">
                <span className="settings-policy-title">Sign-in by emailed code, no password</span>
                <span className="settings-hint">
                  On, a mailed code alone signs a client in - even where a password exists, so
                  anyone who can read the client's mailbox can enter. Off, a code only ever finishes
                  a password sign-in.
                </span>
              </span>
            </label>

            <label className="settings-policy">
              <input
                type="checkbox"
                aria-label="Sign-in by case ID and date of birth"
                checked={caseIdDoor}
                disabled={busy}
                onChange={(e) =>
                  setEdits((prev) => ({ ...prev, caseIdLoginEnabled: e.target.checked }))
                }
              />
              <span className="settings-policy-switch" aria-hidden="true" />
              <span className="settings-policy-text">
                <span className="settings-policy-title">Sign-in by case ID and date of birth</span>
                <span className="settings-hint">
                  On, the case ID the office gave a client and their date of birth open the portal
                  wherever access is on, invited or not. Off, only an account opens it, so the door
                  closes only once the firm's outgoing mail is proven - a client without a password
                  has no other way in.
                </span>
              </span>
            </label>
          </div>

          {status.source === "db" && status.updatedAt && (
            <p className="settings-hint">
              Saved {new Date(status.updatedAt).toLocaleDateString()}
              {status.updatedByEmail ? ` by ${status.updatedByEmail}` : ""}
            </p>
          )}

          <div className="settings-actions">
            <button
              type="button"
              className="settings-save-btn"
              disabled={!dirty || busy}
              onClick={onSave}
            >
              {save.isPending ? "Saving..." : "Save policy"}
            </button>
            {status.source === "db" && (
              <button
                type="button"
                className="cc-link-btn danger"
                disabled={busy}
                onClick={onRemove}
              >
                Remove settings
              </button>
            )}
          </div>

          {save.isError && <p className="settings-error">{(save.error as Error).message}</p>}
          {remove.isError && <p className="settings-error">{(remove.error as Error).message}</p>}
          {save.isSuccess && !dirty && (
            <p className="settings-ok" role="status">
              Policy saved.
            </p>
          )}
        </>
      )}
    </section>
  );
}
